import { toast } from '../utils/toast';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';

export interface NASUser {
  username: string;
  email: string;
  isAdmin?: boolean;
}

export interface AuthResult {
  user: NASUser;
  token: string;
  success: boolean;
}

class NASAuthService {
  private token: string | null = null;

  constructor() {
    // 로컬 스토리지에서 토큰 복원
    this.token = localStorage.getItem('nas_token');
  }

  // JWT 토큰 디코딩
  private decodeToken(token: string): any {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('토큰 디코딩 실패:', error);
      return null;
    }
  }

  // 토큰 유효성 검사
  private isTokenValid(token: string): boolean {
    const payload = this.decodeToken(token);
    if (!payload) return false;
    
    const currentTime = Math.floor(Date.now() / 1000);
    return payload.exp > currentTime;
  }

  // 재시도를 위한 유틸리티 함수
  private async retryOperation<T>(
    operation: () => Promise<T>,
    maxRetries: number = 3,
    retryDelay: number = 1000
  ): Promise<T> {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await operation();
      } catch (error) {
        if (attempt === maxRetries) {
          throw error;
        }
        
        // 인증 오류(401, 403)는 재시도하지 않음
        if (error instanceof Error && (
          error.message.includes('잘못된 사용자명') ||
          error.message.includes('비밀번호') ||
          error.message.includes('인증') ||
          error.message.includes('권한')
        )) {
          throw error; // 즉시 실패
        }
        
        // 네트워크 오류나 서버 오류인 경우에만 재시도
        if (error instanceof TypeError || 
            (error instanceof Error && (
              error.message.includes('네트워크') ||
              error.message.includes('연결') ||
              error.message.includes('timeout') ||
              error.message.includes('fetch') ||
              error.message.includes('서버 오류') ||
              error.message.includes('서버에 연결')
            ))) {
          console.warn(`로그인 시도 ${attempt}/${maxRetries} 실패, ${retryDelay}ms 후 재시도...`);
          await this.delay(retryDelay);
          retryDelay *= 1.5; // 지수 백오프
        } else {
          // 기타 오류는 즉시 실패
          throw error;
        }
      }
    }
    throw new Error('재시도 실패');
  }

  // 지연 함수
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // 로그인
  async login(username: string, password: string): Promise<AuthResult> {
    
    // 재시도 없이 단일 요청으로 처리
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5초 타임아웃으로 단축
      const startTime = performance.now();

      const response = await fetch(`${API_BASE_URL}/auth.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      const duration = Math.round(performance.now() - startTime);

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('잘못된 사용자명 또는 비밀번호입니다.');
        } else if (response.status === 503) {
          throw new Error('서버에 연결할 수 없습니다. 잠시 후 다시 시도해주세요.');
        } else if (response.status >= 500) {
          throw new Error('서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.');
        }
        
        try {
          const errorData = await response.json();
          throw new Error(errorData.error || '로그인에 실패했습니다.');
        } catch (e) {
          throw new Error('로그인에 실패했습니다.');
        }
      }

      const data = await response.json();
      
      if (data.success && data.token) {
        this.token = data.token;
        localStorage.setItem('nas_token', data.token);
        
        // 서버에서 받은 사용자 정보에 isAdmin 추가
        const userWithAdmin = {
          ...data.user,
          isAdmin: data.user.username === 'kikafeel'
        };
        
        
        return {
          user: userWithAdmin,
          token: data.token,
          success: true
        };
      } else {
        console.error('❌ 인증 응답 오류:', data);
        throw new Error(data.error || '인증 응답이 올바르지 않습니다.');
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        throw new Error('요청 시간이 초과되었습니다. 네트워크 연결을 확인해주세요.');
      }
      
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('네트워크 연결을 확인해주세요.');
      }
      
      console.error('NAS 로그인 실패:', error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('알 수 없는 오류가 발생했습니다.');
    }
  }

  // 로그아웃
  logout(): void {
    this.token = null;
    localStorage.removeItem('nas_token');
  }

  // 현재 사용자 정보 가져오기
  getCurrentUser(): NASUser | null {
    if (!this.token || !this.isTokenValid(this.token)) {
      this.logout();
      return null;
    }

    const payload = this.decodeToken(this.token);
    if (!payload) return null;

    const user = {
      username: payload.username,
      email: payload.email || `${payload.username}@nas.local`,
      isAdmin: payload.username === 'kikafeel'
    };

    return user;
  }

  // 토큰 가져오기
  getToken(): string | null {
    if (!this.token || !this.isTokenValid(this.token)) {
      this.logout();
      return null;
    }
    return this.token;
  }

  // 인증 상태 확인
  isAuthenticated(): boolean {
    return this.token !== null && this.isTokenValid(this.token);
  }

  // API 요청에 인증 헤더 추가
  getAuthHeaders(): Record<string, string> {
    const token = this.getToken();
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  }

  // 관리자 권한 확인
  isAdmin(): boolean {
    const user = this.getCurrentUser();
    return user?.isAdmin || false;
  }

  // 관리자 권한 확인 (사용자명으로)
  isAdminUser(username: string): boolean {
    return username === 'kikafeel';
  }

  // 세션 확인 (authStore에서 호출됨)
  async checkSession(): Promise<NASUser | null> {
    try {
      if (!this.token || !this.isTokenValid(this.token)) {
        return null;
      }

      const response = await fetch(`${API_BASE_URL}/auth.php?action=check`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        return null;
      }

      const data = await response.json();
      
      if (data.success && data.user) {
        return {
          ...data.user,
          isAdmin: data.user.username === 'kikafeel'
        };
      }

      return null;
    } catch (error) {
      console.error('세션 확인 실패:', error);
      return null;
    }
  }

  // 토큰 유효성 검증 (authStore에서 호출됨)
  async validateToken(token: string): Promise<boolean> {
    try {
      if (!token || !this.isTokenValid(token)) {
        return false;
      }

      const response = await fetch(`${API_BASE_URL}/auth.php?action=validate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
      });

      if (!response.ok) {
        return false;
      }

      const data = await response.json();
      return data.success === true;
    } catch (error) {
      console.error('토큰 검증 실패:', error);
      return false;
    }
  }

  // 토큰 갱신 (authStore에서 호출됨)
  async refreshToken(currentToken: string): Promise<string> {
    try {
      const response = await fetch(`${API_BASE_URL}/auth.php?action=refresh`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${currentToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token: currentToken }),
      });

      if (!response.ok) {
        throw new Error('토큰 갱신에 실패했습니다.');
      }

      const data = await response.json();
      
      if (data.success && data.token) {
        this.token = data.token;
        localStorage.setItem('nas_token', data.token);
        return data.token;
      } else {
        throw new Error('토큰 갱신 응답이 올바르지 않습니다.');
      }
    } catch (error) {
      console.error('토큰 갱신 실패:', error);
      throw error;
    }
  }
}

export const nasAuthService = new NASAuthService();
export default nasAuthService;